// "You are close to your plan limit" — the admin shell's usage banner
// (docs/plan-limits-plan.md §7). OWNER and MANAGER see it when any metric is at
// 80 % or more (amber) or 100 % or more (red); CASHIER and platform users never
// do, and never trigger the request. Usage is read again whenever the profile is
// refreshed, and otherwise every 5 minutes. Dismissing hides it for this browser
// session, until the usage picture changes.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { UsageMetricSnapshot } from '@bbpos/types';
import type { TenantUsage } from '@/types/tenant';
import { tenantService } from '@/services/tenant.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { cn } from '@/components/features/subscriptions/design-tokens';
import {
  USAGE_METRIC_LABELS,
  usageTone,
} from '@/components/features/subscriptions/plan-entitlements';

const REFRESH_MS = 5 * 60 * 1000;
const DISMISS_KEY = 'bbpos_usage_banner_dismissed';
const BANNER_ROLES = ['OWNER', 'MANAGER'];

type Metric = UsageMetricSnapshot['metric'];

export interface UsageAlert {
  level: 'warning' | 'danger';
  /** At or over 100 %. */
  reached: Metric[];
  /** From 80 % up to 100 %. */
  near: Metric[];
}

/** Which metrics need a warning; null when everything is under 80 %. */
export function usageAlert(metrics: UsageMetricSnapshot[]): UsageAlert | null {
  const reached = metrics.filter((m) => usageTone(m.percent) === 'danger').map((m) => m.metric);
  const near = metrics.filter((m) => usageTone(m.percent) === 'warning').map((m) => m.metric);
  if (reached.length === 0 && near.length === 0) return null;
  return { level: reached.length > 0 ? 'danger' : 'warning', reached, near };
}

/** "bills", "bills and reports", "bills, reports and messages". */
function nameList(metrics: Metric[]): string {
  const names = metrics.map((m) => USAGE_METRIC_LABELS[m].toLowerCase());
  const last = names[names.length - 1];
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${last}` : last;
}

/** The banner's sentences, in simple English. */
export function usageAlertMessage(alert: UsageAlert): string {
  const parts: string[] = [];
  if (alert.reached.length > 0) {
    parts.push(`You have reached your plan limit for ${nameList(alert.reached)}.`);
    if (alert.reached.includes('invoices')) parts.push('You can still make bills.');
  }
  if (alert.near.length > 0) {
    parts.push(
      alert.reached.length > 0
        ? `You are close to the limit for ${nameList(alert.near)}.`
        : `You are close to your plan limit for ${nameList(alert.near)}.`,
    );
  }
  return parts.join(' ');
}

/** Changes when the period, the level or the metrics change, so a new warning shows again. */
function alertKey(period: string, alert: UsageAlert): string {
  return `${period}:${alert.level}:${alert.reached.join(',')}:${alert.near.join(',')}`;
}

export function UsageBanner() {
  const profile = useAuthStore((s) => s.profile);
  const tenantId = profile?.tenant_id;
  const isOwner = profile?.role === 'OWNER';
  const enabled = Boolean(tenantId) && BANNER_ROLES.includes(profile?.role ?? '');

  const [usage, setUsage] = useState<TenantUsage | null>(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY));

  // Re-runs whenever the profile object is replaced (sign-in, profile refresh).
  useEffect(() => {
    if (!enabled || !tenantId) {
      setUsage(null);
      return;
    }
    let active = true;
    const load = () => {
      tenantService
        .getUsage(tenantId)
        .then((next) => {
          if (active) setUsage(next);
        })
        .catch(() => {
          // No banner is better than a broken one; the next refresh tries again.
        });
    };
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [enabled, tenantId, profile]);

  const alert = usage ? usageAlert(usage.metrics) : null;
  if (!enabled || !usage || !alert) return null;
  const key = alertKey(usage.period, alert);
  if (dismissed === key) return null;

  const danger = alert.level === 'danger';
  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, key);
    setDismissed(key);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
      <div
        role="status"
        data-testid="usage-banner"
        data-level={alert.level}
        className={cn(
          'animate-fade-in flex items-start gap-4 rounded-xl border p-4 shadow-md',
          danger
            ? 'border-rose-200 bg-gradient-to-r from-rose-50 to-orange-50 dark:border-rose-800 dark:from-rose-900/30 dark:to-orange-900/20'
            : 'border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 dark:border-amber-800 dark:from-amber-900/30 dark:to-yellow-900/20',
        )}
      >
        <div
          className={cn(
            'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm',
            danger ? 'from-rose-500 to-orange-500' : 'from-amber-400 to-orange-500',
          )}
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <path d="M12 9v4M12 17h.01" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-sm font-semibold',
              danger ? 'text-rose-800 dark:text-rose-200' : 'text-amber-800 dark:text-amber-200',
            )}
          >
            {usageAlertMessage(alert)}
          </p>
          <p
            className={cn(
              'mt-0.5 text-xs',
              danger ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300',
            )}
          >
            {isOwner ? 'Upgrade your plan to get more.' : 'Ask your owner to upgrade.'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isOwner && (
            <Link
              to="/my-subscription"
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:shadow-md',
                danger
                  ? 'bg-gradient-to-r from-rose-600 to-orange-600'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500',
              )}
            >
              See my plan
            </Link>
          )}
          <button
            type="button"
            onClick={dismiss}
            aria-label="Hide this message"
            className="rounded-lg p-1.5 text-neutral-500 transition-all duration-200 hover:bg-white/70 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
